import { ComponentType, OverlayRef } from '@angular/cdk/overlay';
import { ComponentRef, computed, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';


export interface OverlayCloseEvent<T = any> {
  type: 'backdropClick' | 'close';
  data: T;
}
export interface OverlaySendEvent<Y> {
  type: 'submitClick';
  data: Y;
}

export class MyOverlayRef<R = any, T = any, Y = any> {
  private _afterClosed = signal<OverlayCloseEvent | null>(null);
  private _receiveData = signal<OverlaySendEvent<Y> | null>(null);
  private _isClosed = signal<boolean>(false);

  // Public readonly signals
  readonly afterClosed = this._afterClosed.asReadonly();
  readonly receiveData = this._receiveData.asReadonly();
  readonly isClosed = this._isClosed.asReadonly();

  // Computed signals for derived state
  readonly hasClosedEvent = computed(() => !!this._afterClosed());
  readonly hasReceivedData = computed(() => !!this._receiveData());
  readonly isOverlayClosed = computed(() => this._isClosed());

  // Backward compatibility: Observable versions for existing code
  afterClosed$ = new Subject<OverlayCloseEvent>();
  receiveData$ = new Subject<OverlaySendEvent<Y>>();
  onClose$: Subject<boolean> = new Subject();

  constructor(
    public overlay: OverlayRef,
    public content: ComponentType<R>,
    public data: T,
    public componentInstance?: ComponentRef<R>
  ) {
  }

  close(data?: T) {
    this._close('close', data);
  }

  send(data?: Y) {
    if (data !== undefined) {
      this._sendData('submitClick', data);
    }
  }

  backdropClick(): Observable<MouseEvent> {
    return this.overlay.backdropClick();
  }

  activateCloseOnBackdropClick() {
    return this.overlay.backdropClick().pipe(
      takeUntil(this.onClose$)
    ).subscribe(() => this._close('backdropClick', undefined));
  }

  activateCloseOnEscapeClick() {
    return this.overlay.keydownEvents().pipe(
      filter(event => event?.keyCode?.toString() === '27' || event?.key?.toString() === 'Escape'),
      takeUntil(this.onClose$)
    ).subscribe(() => this._close('backdropClick', undefined));
  }

  private _close(type: 'backdropClick' | 'close', data: T | undefined) {
    this.overlay.dispose();

    // Update signals
    this._afterClosed.set({ type, data: data as T });
    this._isClosed.set(true);

    // Emit to Observables for backward compatibility
    this.afterClosed$.next({ type, data: data as T });
    this.afterClosed$.complete();
    this.receiveData$.complete();
    this.onClose$.next(true);
    this.onClose$.complete();
  }

  private _sendData(type: 'submitClick', data: Y) {
    // Update signal
    this._receiveData.set({ type, data });

    // Emit to Observable for backward compatibility
    this.receiveData$.next({ type, data });
  }
}
